/**
* @author Lee Stemkoski   http://www.adelphi.edu/~stemkoski/
*/
import * as THREE from './three.module.js';
///////////////////////////////////////////////////////////////////////////////

/////////////
// SHADERS //
/////////////

// attribute: data that may be different for each particle (such as size and color);
//      can only be used in vertex shader
// varying: used to communicate data from vertex shader to fragment shader
// uniform: data that is the same for each particle (such as texture)


const particleVertexShader = 
[
"attribute vec3  customColor;",
"attribute float customOpacity;",
"attribute float customSize;",
"attribute float customAngle;",
"attribute float customVisible;",  // float used as boolean (0 = false, 1 = true)
"varying vec4  vColor;",
"varying float vAngle;",
"void main()",
"{",
	
	"if ( customVisible > 0.5 )", 				// true
		"vColor = vec4( customColor, customOpacity );", //     set color associated to vertex; use later in fragment shader.
	"else",							// false
		"vColor = vec4(0.0, 0.0, 0.0, 0.0);", 		//     make particle invisible.

	"vAngle = customAngle;",
	
	"vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );",
	"gl_PointSize = customSize * ( 200.0 / mvPosition.w );",     // scale particles as objects in 3D space
	"gl_Position = projectionMatrix * mvPosition;",
"}"
].join("\n");

const particleFragmentShader =
[
"uniform sampler2D tex;",
"varying vec4 vColor;", 	
"varying float vAngle;",   
"void main()", 
"{",
	//"gl_FragColor = vColor;",

	"if(vColor.a <0.1){ discard; return; }",
	"float c = cos(vAngle);",
	"float s = sin(vAngle);",
	"vec2 rotatedUV = vec2(c * (gl_PointCoord.x - 0.5) + s * (gl_PointCoord.y - 0.5) + 0.5,", 
	                      "c * (gl_PointCoord.y - 0.5) - s * (gl_PointCoord.x - 0.5) + 0.5);",  // rotate UV coordinates to rotate texture
    "vec4 rotatedTexture = texture2D( tex,  rotatedUV );",
	"gl_FragColor = vColor * rotatedTexture;",    // sets an otherwise white particle texture to desired color
	
"}"
].join("\n");

///////////////////////////////////////////////////////////////////////////////

/////////////////
// TWEEN CLASS //
/////////////////



class Tween{

	constructor(timeArray, valueArray)
	{
		this.times  = timeArray || [];
		this.values = valueArray || [];
	}

	lerp (t)
	{
		var i = 0;
		var n = this.times.length;
		while (i < n && t > this.times[i])  
			i++;
		if (i == 0) return this.values[0];
		if (i == n)	return this.values[n-1];
		var p = (t - this.times[i-1]) / (this.times[i] - this.times[i-1]);
		return this.values[i-1] + p * (this.values[i] - this.values[i-1]);
	}

	lerp3 (t, col)
	{
		var i = 0;
		var n = this.times.length;
		while (i < n && t > this.times[i])  
			i++;
		if (i == 0) return this.values[0];
		if (i == n)	return this.values[n-1];
		const p = (t - this.times[i-1]) / (this.times[i] - this.times[i-1]);

		tv3.lerpVectors(this.values[i-1], this.values[i], p );
		col.setRGB(tv3.x,tv3.y,tv3.z);
	}
}

///////////////////////////////////////////////////////////////////////////////

////////////////////
// PARTICLE CLASS //
////////////////////

const tv3 = new THREE.Vector3();
const texCache = [];
async function loadParticleTexture(textureURL){

	if(texCache[textureURL])
		return;

	texCache[textureURL] = await new THREE.TextureLoader().load(textureURL);
}

class Particle
{
	constructor()
	{
		this.position     = new THREE.Vector3();
		this.velocity     = new THREE.Vector3(); // units per second
		this.acceleration = new THREE.Vector3();

		this.angle             = 0;
		this.angleVelocity     = 0; // degrees per second
		this.angleAcceleration = 0; // degrees per second, per second
		
		this.size = 16.0;

		this.color   = new THREE.Color();
		this.opacity = 1.0;
				
		this.age   = 0;
		this.alive = 0; // use float instead of boolean for shader purposes	

		//console.log('new particle');
	}

	update (dt, colorTween, sizeTween,  opacityTween, log)
	{	
	
		tv3.set(this.velocity.x, this.velocity.y, this.velocity.z).multiplyScalar(dt) 
		this.position.add( tv3 );

		/*
		if(log){
			console.log('velocity '+ tv3.x + ' ' + tv3.y + ' ' + tv3.z);
			console.log('position '+ this.position.x + ' ' + this.position.y + ' ' + this.position.z);
		}
		*/

		tv3.set(this.acceleration.x, this.acceleration.y, this.acceleration.z).multiplyScalar(dt) 
		this.velocity.add( tv3 );
		
		// convert from degrees to radians: 0.01745329251 = Math.PI/180
		this.angle         += this.angleVelocity     * 0.01745329251 * dt;
		this.angleVelocity += this.angleAcceleration * 0.01745329251 * dt;

		this.age += dt;
		
		// if the tween for a given attribute is nonempty,
		//  then use it to update the attribute's value

		if ( sizeTween.times.length > 0 )
			this.size = sizeTween.lerp( this.age );
					
		if ( colorTween.times.length > 0 )
			colorTween.lerp3( this.age, this.color );
		
		if ( opacityTween.times.length > 0 )
			this.opacity = opacityTween.lerp( this.age );
	}
}
	
///////////////////////////////////////////////////////////////////////////////

///////////////////////////
// PARTICLE ENGINE CLASS //
///////////////////////////

const Type = Object.freeze({ "CUBE":1, "SPHERE":2 });
let partId = 1;

class ParticleEngine
{	
	constructor(positionBase, pinned = false)
	{
		/////////////////////////
		// PARTICLE PROPERTIES //
		/////////////////////////
		
		this.positionStyle = Type.CUBE;		
		this.positionBase   = positionBase.clone();
		this.lastPositionBase   = this.positionBase.clone();
		// cube shape data
		this.positionSpread = new THREE.Vector3();
		this.originVec = new THREE.Vector3();
		// sphere shape data
		this.positionRadius = 0; // distance from base at which particles start
		this.nnew = 0;
		this.velocityStyle = Type.CUBE;	
		// cube movement data
		this.velocityBase       = new THREE.Vector3();
		this.velocitySpread     = new THREE.Vector3(); 
		// sphere movement data
		//   direction vector calculated using initial position
		this.speedBase   = 0;
		this.speedSpread = 0;

		this.pinned = pinned;

		this.id = partId++;
		
		this.accelerationBase   = new THREE.Vector3();
		this.accelerationSpread = new THREE.Vector3();	
		
		this.angleBase               = 0;
		this.angleSpread             = 0;
		this.angleVelocityBase       = 0;
		this.angleVelocitySpread     = 0;
		this.angleAccelerationBase   = 0;
		this.angleAccelerationSpread = 0;
		
		this.sizeBase   = 0.1;
		this.sizeSpread = 0.1;
		this.sizeTween  = new Tween();
				
		// store colors in HSL format in a THREE.Vector3 object
		// http://en.wikipedia.org/wiki/HSL_and_HSV
		this.colorBase   = new THREE.Vector3(1.0, 1.0, 1.0); 
		this.colorSpread = new THREE.Vector3(0.0, 0.0, 0.0);
		this.colorTween  = new Tween();

		this.tmpV3 = new THREE.Vector3()
		this.tmpV31 = new THREE.Vector3()
		this.tmpV32 = new THREE.Vector3()
		
		this.opacityBase   = 1.0;
		this.opacitySpread = 0.0;
		this.opacityTween  = new Tween();

		this.blendStyle = THREE.NormalBlending; // false;

		this.particleArray = [];
		this.particlesPerSecond = 100;
		this.particleDeathAge = 1.0;
		
		////////////////////////
		// EMITTER PROPERTIES //
		////////////////////////
		
		this.emitterAge      = 0.0;
		this.emitterAlive    = true;
		this.emitterDeathAge = 60; // time (seconds) at which to stop creating particles.
		
		// How many particles could be active at any time?
		this.particleCount = this.particlesPerSecond * Math.min( this.particleDeathAge, this.emitterDeathAge );

		//////////////
		// THREE.JS //
		//////////////
		
		this.particleGeometry = new THREE.BufferGeometry();
		this.particleGeometry.setAttribute('position',	new THREE.Float32BufferAttribute([], 3));
		this.particleGeometry.setAttribute('customVisible', new THREE.Float32BufferAttribute([],1));
		this.particleGeometry.setAttribute('customAngle', new THREE.Float32BufferAttribute([],1));
		this.particleGeometry.setAttribute('customSize', new THREE.Float32BufferAttribute([],1));
		this.particleGeometry.setAttribute('customColor',	new THREE.Float32BufferAttribute([],3));
		this.particleGeometry.setAttribute('customOpacity',	new THREE.Float32BufferAttribute([],1));
		

		//this.particleTexture  = new THREE.TextureLoader().load( '/assets/textures/smokeparticle.png' );
		this.particleMaterial = new THREE.ShaderMaterial( 
		{
			uniforms: 
			{
				tex:   { type: "t", value: this.particleTexture }
			},
			vertexShader:   particleVertexShader,
			fragmentShader: particleFragmentShader,
			transparent: true, // alphaTest: 0.5,  // if having transparency issues, try including: alphaTest: 0.5, 
			blending: THREE.NormalBlending, depthWrite:false, depthTest: true,
			
		});
		this.particleMesh = new THREE.Mesh();

		if(!this.pinned)
			this.particleMesh.position.set(this.positionBase.x, this.positionBase.y, this.positionBase.z);
		else
			this.particleMesh.position.set(0, 0, 0);

		this.particlesUv = [[0,0],[0,1],[1,0],[1,1]];
		this.particlesIdxs = [0,1,2,2,1,3];
	}


	
	async setValues ( parameters )
	{
		if ( parameters === undefined ) return;
		
		// clear any previous tweens that might exist
		this.sizeTween    = new Tween();
		this.colorTween   = new Tween();
		this.opacityTween = new Tween();
		
		for ( var key in parameters ) 
			this[ key ] = parameters[ key ];
		
		// attach tweens to particles
		/*
		Particle.prototype.sizeTween    = this.sizeTween;
		Particle.prototype.colorTween   = this.colorTween;
		Particle.prototype.opacityTween = this.opacityTween;	
		*/
		
		// calculate/set derived particle engine values
		this.particleArray = [];
		this.emitterAge      = 0.0;
		this.emitterAlive    = true;
		this.particleCount = this.particlesPerSecond * Math.min( this.particleDeathAge, this.emitterDeathAge );

		this.particleGeometry = new THREE.BufferGeometry();
		this.particleGeometry.setAttribute('position',	new THREE.Float32BufferAttribute(this.particleCount * 3, 3));
		this.particleGeometry.setAttribute('customVisible', new THREE.Float32BufferAttribute(this.particleCount,1));
		this.particleGeometry.setAttribute('customAngle', new THREE.Float32BufferAttribute(this.particleCount,1));
		this.particleGeometry.setAttribute('customSize', new THREE.Float32BufferAttribute(this.particleCount,1));
		this.particleGeometry.setAttribute('customColor',	new THREE.Float32BufferAttribute(this.particleCount * 3,3));
		this.particleGeometry.setAttribute('customOpacity',	new THREE.Float32BufferAttribute(this.particleCount,1));

		
		this.particleTexture  = texCache[this.particleTextureUrl];

		this.particleMaterial = new THREE.ShaderMaterial( 
		{
			uniforms: 
			{
				tex:   { type: "t", value: this.particleTexture },
			},
			vertexShader:   particleVertexShader,
			fragmentShader: particleFragmentShader,
			transparent: true, // alphaTest: 0.5, // if having transparency issues, try including: alphaTest: 0.5, 
			blending: THREE.NormalBlending, depthWrite:false,depthTest: true
		});
		this.particleMesh = new THREE.Points();

		if(!this.pinned)
			this.particleMesh.position.set(this.positionBase.x, this.positionBase.y, this.positionBase.z);

		this.particleMesh.renderOrder = 1000;
	}
	
	// helper functions for randomization
	randomValue (base, spread)
	{
		return base + spread * (Math.random() - 0.5);
	}

	randomVector3 (vec, base, spread)
	{
		this.tmpV3.set( Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5 ).multiply(spread);
		vec.addVectors( base, this.tmpV3 );
	}

	randomColor3 (col, base, spread)
	{
		this.tmpV3.set( Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5 ).multiply(spread);
//		this.tmpV31.multiplyVectors( spread, this.tmpV3 );
		this.tmpV31.addVectors( base, this.tmpV3 );
		col.setRGB(this.tmpV31.x, this.tmpV31.y, this.tmpV31.z);
	}

	resetParticle(particle, lerp = 0.0){

		const basePos = this.lastPositionBase.lerpVectors(this.lastPositionBase, this.pinned ? this.positionBase : this.originVec, lerp);

		

		if (this.positionStyle == Type.CUBE)
			this.randomVector3(particle.position, basePos, this.positionSpread ); 
		if (this.positionStyle == Type.SPHERE)
		{
			var z = 2 * Math.random() - 1;
			var t = 6.2832 * Math.random();
			var r = Math.sqrt( 1 - z*z );
			this.tmpV3.set( r * Math.cos(t), r * Math.sin(t), z );
			particle.position.addVectors( basePos, this.tmpV3.multiplyScalar( this.positionRadius ) );
		}
			
		if ( this.velocityStyle == Type.CUBE )
		{
			this.randomVector3(particle.velocity,  this.velocityBase,     this.velocitySpread ); 

		}
		if ( this.velocityStyle == Type.SPHERE )
		{
			const speed     = this.randomValue( this.speedBase, this.speedSpread );
			particle.velocity.subVectors( particle.position, this.pinned ? this.positionBase : this.originVec ).normalize().multiplyScalar( speed );
		}
		
		this.randomVector3(particle.acceleration, this.accelerationBase, this.accelerationSpread ); 

		particle.angle             = this.randomValue( this.angleBase,             this.angleSpread );
		particle.angleVelocity     = this.randomValue( this.angleVelocityBase,     this.angleVelocitySpread );
		particle.angleAcceleration = this.randomValue( this.angleAccelerationBase, this.angleAccelerationSpread );

		particle.size = this.randomValue( this.sizeBase, this.sizeSpread );

		this.randomColor3(particle.color, this.colorBase, this.colorSpread );
		
		particle.opacity = this.randomValue( this.opacityBase, this.opacitySpread );

		particle.age   = 0;
		particle.alive = 0; // particles initialize as inactive
	}

	createParticle()
	{
		const particle = new Particle();
		this.resetParticle(particle);
		return particle;
	}

	initialize()
	{

		this.recycleIndices = new Uint32Array(this.particleCount + 1);
		this.nReclycable = 0;
		// link particle data with geometry/material data
		for (var i = 0; i < this.particleCount; i++)
		{
			// remove duplicate code somehow, here and in update function below.
			this.particleArray[i] = this.createParticle();
			
			this.particleGeometry.attributes.position.array[i*3+0] = this.particleArray[i].position.x;
			this.particleGeometry.attributes.position.array[i*3+1] = this.particleArray[i].position.y;
			this.particleGeometry.attributes.position.array[i*3+2] = this.particleArray[i].position.z;

			this.particleGeometry.attributes.customVisible.array[i] = this.particleArray[i].alive;

			this.particleGeometry.attributes.customColor.array[i *3+0]   = this.particleArray[i].color.r;
			this.particleGeometry.attributes.customColor.array[i *3+1]   = this.particleArray[i].color.g;
			this.particleGeometry.attributes.customColor.array[i *3+2]   = this.particleArray[i].color.b;
			

			this.particleGeometry.attributes.customOpacity.array[i] = this.particleArray[i].opacity;
			this.particleGeometry.attributes.customSize.array[i]    = this.particleArray[i].size;
			this.particleGeometry.attributes.customAngle.array[i]   = this.particleArray[i].angle;
		}
		
		this.particleMaterial.blending = this.blendStyle;
		if ( this.blendStyle != THREE.NormalBlending) 
		{
			this.particleMaterial.depthTest = true;
			this.particleMaterial.depthWrite = false;
		}
		this.particleMesh = new THREE.Points( this.particleGeometry, this.particleMaterial );
		this.totalParticleCreated = 0;

		if(!this.pinned){
			this.particleMesh.position.set(this.positionBase.x, this.positionBase.y, this.positionBase.z);
		}
			
			

		this.particleMesh.name = 'particules Mesh '+this.name;
		this.particleMesh.dynamic = true;
		this.particleMesh.sortParticles = true;
		this.particleMesh.frustumCulled = false;
		this.particleMesh.renderOrder = 1000;
		this.emitterAge      = 0.0;
	}

	update(dt , log = false)
	{
		//dt = Math.min(dt, 0.1);
	    const positions = this.particleGeometry.attributes.position.array;
		const customVisible = this.particleGeometry.attributes.customVisible.array;
		const customAngle = this.particleGeometry.attributes.customAngle.array;
		const customSize = this.particleGeometry.attributes.customSize.array;
		const customColor = this.particleGeometry.attributes.customColor.array;
		const customOpacity = this.particleGeometry.attributes.customOpacity.array;

		if(dt>1)
			return;

		if(!this.pinned){
			this.particleMesh.position.set(this.positionBase.x, this.positionBase.y, this.positionBase.z);
			this.nnew = (this.emitterAge * this.particlesPerSecond ) - this.totalParticleCreated;
		}else{
			this.particleMesh.position.set(0, 0, 0);
			this.nnew += dt * this.particlesPerSecond;
		}
			
		this.nReclycable = 0;
		this.aliveParticles = 0;

		let totalNew = this.nnew;
		
		
		// update particle data
		for (let i = 0; i < this.particleCount; i++)
		{
			if ( this.particleArray[i].alive )
			{
				this.particleArray[i].update(dt, this.colorTween, this.sizeTween, this.opacityTween, log);

				// check if particle should expire
				// could also use: death by size<0 or alpha<0.
				if ( this.particleArray[i].age > this.particleDeathAge ) 
				{
					this.particleArray[i].alive = 0.0;
					this.recycleIndices[this.nReclycable++] = i;
					//this.recycleIndices.push(i);
				}
				this.aliveParticles++;
			
			}else if((this.emitterAlive )&&(this.nnew>0)){
				this.resetParticle(this.particleArray[i], (totalNew - this.nnew) / totalNew);
				this.particleArray[i].alive = 1.0; // activate right away
				this.nnew--;
				this.totalParticleCreated++;
			}
			// update particle properties in shader
		}
		
		// update particle data
		for (let i = 0; i < this.particleCount; i++)
		{
			if ( this.particleArray[i].alive )
			{
				positions[i * 3 + 0]= this.particleArray[i].position.x;
				positions[i * 3 + 1]= this.particleArray[i].position.y;
				positions[i * 3 + 2]= this.particleArray[i].position.z;

				/*
				if(log){
					console.log('particle '+positions[i*3+0] + ' ' + positions[i*3+1]+ ' ' + positions[i*3+2]);
				}
				*/

				customColor[i * 3 + 0]   = this.particleArray[i].color.r;
				customColor[i * 3 + 1]   = this.particleArray[i].color.g;
				customColor[i * 3 + 2]   = this.particleArray[i].color.b;

				customOpacity[i] = this.particleArray[i].opacity;
				customSize[i]    = this.particleArray[i].size;
				customAngle[i]   = this.particleArray[i].angle;
			}

			customVisible[i] = this.particleArray[i].alive ? 1 : 0;
		}

		// check if particle emitter is still running
		if ( !this.emitterAlive ){
			this.particleGeometry.attributes.position.needsUpdate = true;
			this.particleGeometry.attributes.customVisible.needsUpdate = true;
			this.particleGeometry.attributes.customColor.needsUpdate = true;
			this.particleGeometry.attributes.customAngle.needsUpdate = true;
			this.particleGeometry.attributes.customSize.needsUpdate = true;
			this.particleGeometry.attributes.customOpacity.needsUpdate = true;
			return;
		}

		/*
		// if no particles have died yet, then there are still particles to activate
		if ( this.particlesPerSecond  * this.emitterAge < this.particleCount )
		{
			// determine indices of particles to activate
			var startIndex = Math.round( this.particlesPerSecond  *(this.emitterAge +  0) );
			var   endIndex = Math.round( this.particlesPerSecond  *(this.emitterAge + dt) );
			if  ( endIndex > this.particleCount ) 
				endIndex = this.particleCount; 
				
			for (let i = startIndex; i < endIndex; i++)
			{
				this.particleArray[i].alive = 1.0;		
			}

			//console.log('init '+startIndex+ ' '+endIndex);
		}*/

		let nRecyledToRecyle =  this.nnew;
		let nRecyled = 0;

		// if any particles have died while the emitter is still running, we imediately recycle them
		while((this.nReclycable>0) && nRecyled < nRecyledToRecyle)
		{
			const i = this.recycleIndices[--this.nReclycable];
			//this.particleArray[i] = this.createParticle();

			this.resetParticle(this.particleArray[i], (totalNew - this.nnew) / totalNew);
			this.particleArray[i].alive = 1.0; // activate right away

			positions[i*3+0]= this.particleArray[i].position.x;
			positions[i*3+1]= this.particleArray[i].position.y;
			positions[i*3+2]= this.particleArray[i].position.z;

			// update particle properties in shader
			customVisible[i] = this.particleArray[i].alive ? 1 : 0;

			customColor[i *3+0]   = this.particleArray[i].color.r;
			customColor[i *3+1]   = this.particleArray[i].color.g;
			customColor[i *3+2]   = this.particleArray[i].color.b;

			customOpacity[i] = this.particleArray[i].opacity;
			customSize[i]    = this.particleArray[i].size;
			customAngle[i]   = this.particleArray[i].angle;

			this.totalParticleCreated++;
			this.nnew--;
			nRecyled++;
		}

		this.particleGeometry.attributes.position.needsUpdate = true;
		this.particleGeometry.attributes.customVisible.needsUpdate = true;
		this.particleGeometry.attributes.customColor.needsUpdate = true;
		this.particleGeometry.attributes.customAngle.needsUpdate = true;
		this.particleGeometry.attributes.customSize.needsUpdate = true;
		this.particleGeometry.attributes.customOpacity.needsUpdate = true;

		this.lastPositionBase.copy( this.pinned ? this.positionBase : this.originVec);

		// stop emitter?
		this.emitterAge += dt;
		if ( this.emitterAge > this.emitterDeathAge )  this.emitterAlive = false;
	}

	destroy ()
	{
    	//this.scene.remove( this.particleMesh );
	}
}


export {ParticleEngine,Particle, Tween, Type, loadParticleTexture};
///////////////////////////////////////////////////////////////////////////////
